import { resources } from "@constants/hardcoded-strings";
import { useModalContext } from "@context/use-modal-context";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { CellDropdownItem } from "@idw-types/common";
import { FormattedAxnAdminUser } from "@idw-types/data/axn-admin-user";
import { useMemo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { TableCellWithDropdown } from "@containers/common/table-cell-with-dropdown/table-cell-with-dropdown";
import { injectAxnAdminUserId } from "@utils/parameter-injector";
import { navigateToUtil } from "@utils/navigation-util";
import { ORGANIZATION_USERS_DETAILS_URL } from "@constants/routes";
import { ModalTypes } from "@enums/modal-types";
import { AxnAdminUserAccountStateTypes } from "@enums/axn-admin-user-account-state-types";
import { deleteOrganizationUserReduxRequest } from "@redux-requests/organization-users/delete-user";
import { restoreOrganizationUserReduxRequest } from "@redux-requests/organization-users/restore-user";
import { archiveOrganizationUserReduxRequest } from "@redux-requests/organization-users/archive-user";
import { UpdateOrganizationUserModal } from "@containers/modals/update-organization-user-status-modal/update-organization-user-status-modal";
import { displaySuccessModal } from "@utils/app-alert-util";
import styles from './organization-users-table-cell.less';

type OrganizationUsersTableCellProps = {
    value: string | React.ReactNode;
    organizationUser: FormattedAxnAdminUser
}

export const OrganizationUsersTableCell = (props: OrganizationUsersTableCellProps) => {
    const { value, organizationUser } = props;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const modalContext = useModalContext();

    const onClickRestoreUser = () => {
        const resource = resources.modals.restoreOrganizationUser;
        const onSubmit = async (onModalClose: () => void) => {
            const response = await restoreOrganizationUserReduxRequest(dispatch)(organizationUser.id);
            if (response) {
                displaySuccessModal(
                    dispatch,
                    resources.requests.restoreOrganizationUser.success.title,
                    resources.requests.restoreOrganizationUser.success.message
                );
                onModalClose();
            }
        }

        renderConfirmUpdateUserStatusModal(resource.title, AxnAdminUserAccountStateTypes.RESTORE, resource.description, onSubmit);
    }

    const onClickDeleteUser = () => {
        const resource = resources.modals.deleteOrganizationUser;
        const onSubmit = async (onModalClose: () => void) => {
            const response = await deleteOrganizationUserReduxRequest(dispatch)(organizationUser.id);
            if (response) {
                displaySuccessModal(
                    dispatch,
                    resources.requests.deleteOrganizationUser.success.title,
                    resources.requests.deleteOrganizationUser.success.message
                );
                onModalClose();
            }
        }

        renderConfirmUpdateUserStatusModal(resource.title, AxnAdminUserAccountStateTypes.DELETE, resource.description, onSubmit);
    }

    const onClickArchiveUser = () => {
        const resource = resources.modals.archiveOrganizationUser;
        const onSubmit = async (onModalClose: () => void) => {
            const response = await archiveOrganizationUserReduxRequest(dispatch)(organizationUser.id);
            if (response) {
                displaySuccessModal(
                    dispatch,
                    resources.requests.archiveOrganizationUser.success.title,
                    resources.requests.archiveOrganizationUser.success.message
                );
                onModalClose();
            }
        }

        renderConfirmUpdateUserStatusModal(resource.title, AxnAdminUserAccountStateTypes.ARCHIVE, resource.description, onSubmit);
    }

    const renderConfirmUpdateUserStatusModal = (
        title: string,
        userStatus: AxnAdminUserAccountStateTypes,
        confirmMessage: string | React.ReactNode,
        onSubmit: (onModalClose: () => void) => Promise<void>
    ) => {

        const renderModal = (onModalClose: () => void) => {
            return (
                <UpdateOrganizationUserModal
                    id={`confirm-${userStatus}-user`}
                    title={title}
                    confirmMessage={confirmMessage}
                    cancelButtonText={resources.common.buttons.cancel.label}
                    submitButtonText={resources.common.buttons.proceed.label}
                    onSubmit={onSubmit}
                    onModalClose={onModalClose}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.CONFIRM,
            renderFunction: renderModal
        });
    };

    const onClickOpenUserDetails = () => {
        const url = injectAxnAdminUserId(ORGANIZATION_USERS_DETAILS_URL, organizationUser.id);
        navigateToUtil(url, navigate, routerLocation.pathname);
    }

    const dropdownItemsResource = resources.tables.organizationUsers.dropdownItems;
    const organizationUserDropdownItems = useMemo(() => {
        const items: Array<CellDropdownItem> = [];
        const { status, permissions } = organizationUser;

        if (permissions.canView) {
            items.push({
                idSuffix: 'open',
                title: dropdownItemsResource.open.label,
                text: dropdownItemsResource.open.label,
                onClick: onClickOpenUserDetails
            });
        }

        if (status === EntityStatusTypes.ACTIVE && permissions.canArchive) {
            items.push({
                idSuffix: 'archive-user',
                title: dropdownItemsResource.inactivateUser.label,
                text: dropdownItemsResource.inactivateUser.label,
                className: styles.archiveButton,
                onClick: onClickArchiveUser
            });
        } else if (status === EntityStatusTypes.INACTIVE && permissions.canRestore) {
            items.push({
                idSuffix: 'restore-user',
                title: dropdownItemsResource.restoreUser.label,
                text: dropdownItemsResource.restoreUser.label,
                onClick: onClickRestoreUser
            });
        }

        if (permissions.canDelete) {
            items.push({
                idSuffix: 'delete-user',
                title: dropdownItemsResource.deleteUser.label,
                text: dropdownItemsResource.deleteUser.label,
                className: styles.archiveButton,
                onClick: onClickDeleteUser
            });
        }

        return items;
    }, [organizationUser]);


    return (
        <TableCellWithDropdown
            value={value}
            dropdownText={""}
            items={organizationUserDropdownItems}
            uniqueId={organizationUser.id}
        />
    );
}